import * as React from 'react';
import useBusiness from 'src/hooks/useBusiness';
import Loader from 'src/components/molecules/Loader';
import {
  CustomerBusinessI,
  CustomerBusinessStatus,
} from 'blum-rivendell-frontend/dist/customer/domain/CustomerBusiness';
import SelectCompany from 'src/components/organisms/forms/SelectCompany';
import { PayloadI } from 'src/business/Customer/domain/Payload';
import { useRouter } from 'next/router';
import { privateRoutes } from 'src/routes';
import { Campaign } from 'src/business/Referral/domain/Campaign';

interface CustomerForSelect extends Pick<CustomerBusinessI, 'id' | 'name'> {}

export interface AuthContextProps {
  isAuthenticated: boolean | undefined;
  isLoading: boolean;
  customer: CustomerBusinessI | null;
  customers: CustomerForSelect[];
  selectCustomer: (customerId: string) => void;
  isLoadingReadCustomer: boolean;
  reloadCustomer: () => void;
  payload: PayloadI;
  reloadPayload: () => void;
  setIsAuthenticated: (boolean) => void;
  onSignIn: (email: string) => void;
  onSignOut: () => void;
  hasActiveCampaign: () => boolean;
}

export const AuthContext = React.createContext<AuthContextProps | undefined>(
  undefined,
);

export const AuthContextProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const [customers, setCustomers] = React.useState<CustomerForSelect[]>([]);
  const [isAuthenticated, setIsAuthenticated] = React.useState<
    boolean | undefined
  >(undefined);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const router = useRouter();

  const { data: campaigns, run: runFindCampaigns } = useBusiness<Campaign[]>({
    initState: null,
  });

  const {
    data: customer,
    isLoading: isLoadingReadCustomer,
    run: runReadCustomer,
    resetData: resetCustomer,
  } = useBusiness<CustomerBusinessI>({
    initState: null,
    onSuccess(data) {
      runFindCampaigns((b) => b.referral.findCampaigns(data.id));
    },
  });

  const enableNewOnboarding =
    process.env.NEXT_PUBLIC_ENABLE_NEW_ONBOARDING === 'true';

  const onboardingPath = enableNewOnboarding
    ? privateRoutes.onboarding_b2b.path
    : privateRoutes.onboarding.path;

  React.useEffect(() => {
    if (customer) {
      const isOnboardingRoute =
        router.pathname === privateRoutes.onboarding.path ||
        router.pathname.startsWith(privateRoutes.onboarding_b2b.path);

      if (
        customer.status === CustomerBusinessStatus.ONBOARDING &&
        !isOnboardingRoute &&
        router.pathname !== privateRoutes.home.path
      ) {
        router.replace(onboardingPath);
      }

      if (
        customer.status !== CustomerBusinessStatus.ONBOARDING &&
        isOnboardingRoute
      ) {
        router.replace(privateRoutes.home.path);
      }

      const isOldOnboarding = router.pathname === privateRoutes.onboarding.path;
      const isNewOnboarding = router.pathname.startsWith(
        privateRoutes.onboarding_b2b.path,
      );

      if (enableNewOnboarding && isOldOnboarding) {
        router.replace(privateRoutes.onboarding_b2b.path);
      } else if (!enableNewOnboarding && isNewOnboarding) {
        router.replace(privateRoutes.onboarding.path);
      }
    }
  }, [customer, router.pathname, enableNewOnboarding, onboardingPath]);

  const { run: runCurrentCustomer } = useBusiness();

  const { run: runGetCustomersForSelect, isLoading: isLoadingGetCustomers } =
    useBusiness();

  const getCustomersForSelect = (ids: string[]) => {
    runGetCustomersForSelect(async (b) => {
      const customers = await Promise.allSettled(
        ids.map((id) => b.customerRivendell.getCustomer(id)),
      );

      const validCustomers = customers
        .filter(
          (c): c is PromiseFulfilledResult<any> => c.status === 'fulfilled',
        )
        .map((c) => ({
          id: c.value.id,
          name: c.value.name,
        }));

      setCustomers(validCustomers);
    });
  };

  const {
    data: dataPayload,
    isLoading: isLoadingPayload,
    run: runGetPayload,
  } = useBusiness({
    initRun: (b) => b.customer.getPayload(),
    onSuccess: (data) => {
      getCustomersForSelect(data['custom:customerIds'].split(','));
      setIsAuthenticated(true);
      setIsLoading(false);
    },
    onError: () => {
      setIsAuthenticated(false);
      setIsLoading(false);
    },
  });

  const { run: runSignOut } = useBusiness({
    onSuccess: () => {
      setIsAuthenticated(false);
    },
  });

  const onSignOut = async () => {
    try {
      window?.$zoho?.salesiq?.reset?.();
    } catch {
      console.log('error en zoho');
    }
    await runSignOut((b) => b.customer.signOut());
    await runCurrentCustomer((b) => {
      b.currentCustomer.reset();
      b.currentCustomerRivendell.reset();
    });
    resetCustomer();
    setCustomers([]);
  };

  const onSignIn = async (email?: string) => {
    try {
      window?.$zoho?.salesiq?.visitor?.email?.(email || '');
    } catch {
      console.log('error en zoho');
    }
    runGetPayload((b) => b.customer.getPayload());
    setIsAuthenticated(true);
  };

  const reloadPayload = async () => {
    runGetPayload((b) =>
      b.customer.getPayload({
        updatedPayload: true,
      }),
    );
  };

  const reloadCustomer = async () => {
    runReadCustomer(
      (b) =>
        b.customerRivendell.getCustomer(
          customer.id,
        ) as Promise<CustomerBusinessI>,
    );
  };

  const selectCustomer = (customerId: string) => {
    runCurrentCustomer((b) => {
      b.currentCustomer.updateId(customerId);
      b.currentCustomerRivendell.updateId(customerId);
    });
    runReadCustomer(
      (b) =>
        b.customerRivendell.getCustomer(
          customerId,
        ) as Promise<CustomerBusinessI>,
    );
  };

  const hasActiveCampaign = () => {
    return campaigns?.some((c) => c.isActive);
  };

  if (isLoadingPayload || isLoadingReadCustomer || isLoadingGetCustomers)
    return <Loader />;

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        onSignIn,
        onSignOut,
        setIsAuthenticated,
        customer,
        customers,
        selectCustomer,
        isLoadingReadCustomer,
        reloadCustomer,
        payload: dataPayload,
        reloadPayload,
        hasActiveCampaign,
      }}
    >
      {isAuthenticated && !customer ? <SelectCompany /> : <>{children}</>}
    </AuthContext.Provider>
  );
};

export const useContextAuth = () => {
  const data = React.useContext(AuthContext);

  if (data === undefined) {
    throw new Error('useContextAuth must be used inside AuthContextProvider');
  }

  return data;
};
